import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, type ApiError, type ValidationError } from '@/utils/api';

interface ContentForm {
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl: string;
  duration: number;
  tags: string[];
  category: string;
  totalEpisodes: number;
  episodeNumber: number;
  groupName: string;
  thirdPartyId: string;
  broadcastUrls: string[];
  uploadMethod: 'url' | 'file';
  selectedFile: File | null;
}

const AddContent: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<ContentForm>({
    title: '',
    description: '',
    videoUrl: '',
    thumbnailUrl: '',
    duration: 0,
    tags: [],
    category: '',
    totalEpisodes: 1,
    episodeNumber: 1,
    groupName: '',
    thirdPartyId: '',
    broadcastUrls: [],
    uploadMethod: 'url',
    selectedFile: null
  });
  
  const [tagInput, setTagInput] = useState('');
  const [broadcastUrlInput, setBroadcastUrlInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    console.log('🔍 DEBUG: Form input changed:', { name, value });
    setForm(prev => ({
      ...prev,
      [name]: name === 'duration' || name === 'totalEpisodes' || name === 'episodeNumber' 
        ? parseFloat(value) || 0 : value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors(prev => prev.filter(error => error.field !== name));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddBroadcastUrl = () => {
    if (broadcastUrlInput.trim() && !form.broadcastUrls.includes(broadcastUrlInput.trim())) {
      try {
        new URL(broadcastUrlInput.trim()); // Validate URL
        setForm(prev => ({
          ...prev,
          broadcastUrls: [...prev.broadcastUrls, broadcastUrlInput.trim()]
        }));
        setBroadcastUrlInput('');
      } catch {
        alert('Geçerli bir URL girin');
      }
    }
  };

  const handleRemoveBroadcastUrl = (urlToRemove: string) => {
    setForm(prev => ({
      ...prev,
      broadcastUrls: prev.broadcastUrls.filter(url => url !== urlToRemove)
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü (500MB)
      if (file.size > 500 * 1024 * 1024) {
        alert('Dosya boyutu 500MB\'dan küçük olmalıdır.');
        return;
      }

      // Dosya türü kontrolü
      const allowedTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        alert('Desteklenmeyen dosya türü. Lütfen MP4, AVI, MOV, WMV, FLV veya WebM formatında bir video seçin.');
        return;
      }

      setForm(prev => ({
        ...prev,
        selectedFile: file
      }));

      console.log('🔍 DEBUG: File selected:', file.name, file.size, file.type);
    }
  };

  // Mock function for file upload - replace with actual S3 upload logic
  const uploadFileToS3 = async (file: File): Promise<string> => {
    // This would be replaced with actual S3 upload logic
    console.log('🔍 DEBUG: Mock S3 upload for file:', file.name);
    
    // Simulate upload delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Return mock S3 path
    return `uploads/${Date.now()}-${file.name}`;
  };

  const getFieldError = (fieldName: string): ValidationError | undefined => {
    return validationErrors.find(error => error.field === fieldName);
  };

  const renderFieldError = (fieldName: string): JSX.Element | null => {
    const error = getFieldError(fieldName);
    if (!error) return null;
    
    return (
      <p className="mt-1 text-sm text-red-600">
        {error.message}
      </p>
    );
  };

  const getFieldClassName = (fieldName: string, baseClassName: string): string => {
    const hasError = getFieldError(fieldName);
    if (hasError) {
      return `${baseClassName} border-red-300 focus:ring-red-500 focus:border-red-500`;
    }
    return `${baseClassName} border-gray-300 focus:ring-blue-500 focus:border-blue-500`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setUploadProgress(0);
    setErrorMessage('');
    setValidationErrors([]);

    try {
      let mediaUrl = form.videoUrl;
      let mediaFilePath = undefined;

      // Handle file upload if file is selected
      if (form.uploadMethod === 'file' && form.selectedFile) {
        setUploadProgress(10);
        console.log('🔍 DEBUG: Uploading file:', form.selectedFile.name);
        
        try {
          mediaFilePath = await uploadFileToS3(form.selectedFile);
          mediaUrl = ''; // Clear URL since we're using file upload
          setUploadProgress(50);
          console.log('🔍 DEBUG: File uploaded successfully:', mediaFilePath);
        } catch (uploadError) {
          console.error('🔍 DEBUG: File upload failed:', uploadError);
          setSubmitStatus('error');
          setErrorMessage('Dosya yükleme sırasında hata oluştu.');
          return;
        }
      }

      setUploadProgress(60);

      // Backend'in beklediği format
      const contentData = {
        title: form.title,
        description: form.description,
        contentType: form.category as 'movie' | 'series' | 'documentary' | 'live',
        totalEpisodes: form.totalEpisodes || 1,
        episodeNumber: form.episodeNumber || 1,
        groupName: form.groupName || undefined,
        thirdPartyId: form.thirdPartyId || undefined,
        broadcastUrls: form.broadcastUrls.length > 0 ? form.broadcastUrls : undefined,
        mediaUrl: mediaUrl || undefined,
        mediaFilePath: mediaFilePath || undefined,
        mediaType: form.selectedFile ? 'mp4' as const : 'hls' as const,
        durationSeconds: form.duration,
        thumbnailUrl: form.thumbnailUrl || undefined,
        tags: form.tags.length > 0 ? form.tags : undefined,
        transcribeEnabled: true
      };

      console.log('🔍 DEBUG: Submitting content data:', JSON.stringify(contentData, null, 2));
      console.log('🔍 DEBUG: Using API endpoint: /content');

      setUploadProgress(80);

      // Use apiClient for proper authentication and CORS
      const responseData = await apiClient.post('/content', contentData);
      
      console.log('🔍 DEBUG: Success response:', responseData);
      setUploadProgress(100);
      setSubmitStatus('success');
      
      // Form'u temizle
      setForm({
        title: '',
        description: '',
        videoUrl: '',
        thumbnailUrl: '',
        duration: 0,
        tags: [],
        category: '',
        totalEpisodes: 1,
        episodeNumber: 1,
        groupName: '',
        thirdPartyId: '',
        broadcastUrls: [],
        uploadMethod: 'url',
        selectedFile: null
      });
      
      // Navigate to the newly created content detail page
      if (responseData && (responseData as any).data && (responseData as any).data.id) {
        console.log('🔍 DEBUG: Navigating to content detail page:', (responseData as any).data.id);
        setTimeout(() => {
          navigate(`/content/${(responseData as any).data.id}`);
        }, 2000);
      } else {
        console.log('🔍 DEBUG: No content ID in response, navigating to content list');
        console.log('🔍 DEBUG: Response structure:', responseData);
        setTimeout(() => {
          navigate('/content');
        }, 2000);
      }
    } catch (error: any) {
      console.error('🔍 DEBUG: Content creation error:', error);
      setSubmitStatus('error');
      
      // Handle API errors with validation details
      if (error.apiError) {
        const apiError = error.apiError as ApiError;
        console.log('🔍 DEBUG: API Error details:', apiError);
        
        if (apiError.details && apiError.details.length > 0) {
          setValidationErrors(apiError.details);
          setErrorMessage(`Formu tamamlamak için lütfen aşağıdaki sorunları düzeltin:`);
        } else {
          setErrorMessage(apiError.message || apiError.error || 'İçerik eklenirken bir hata oluştu.');
        }
      } else {
        setErrorMessage('İçerik eklenirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Yeni İçerik Ekle</h1>
          <p className="text-gray-600">
            Yeni video içeriği ekleyin. Video otomatik olarak analiz edilecek ve shoppable ads için hazırlanacak.
          </p>
        </div>

        {submitStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            ✅ İçerik başarıyla eklendi! Transcribe işlemi başlatıldı.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {errorMessage}
                </h3>
                {validationErrors.length > 0 && (
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc pl-5 space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index}>
                          <span className="font-medium">{error.field}:</span> {error.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {isSubmitting && uploadProgress > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-700">
                {form.uploadMethod === 'file' ? 'Dosya yükleniyor...' : 'İçerik oluşturuluyor...'}
              </span>
              <span className="text-sm text-blue-600">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Başlık */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Video Başlığı *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={form.title}
              onChange={handleInputChange}
              required
              className={getFieldClassName('title', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
              placeholder="Video başlığını girin"
            />
            {renderFieldError('title')}
          </div>

          {/* Açıklama */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Açıklama
            </label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleInputChange}
              rows={4}
              className={getFieldClassName('description', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
              placeholder="Video açıklamasını girin"
            />
            {renderFieldError('description')}
          </div>

          {/* Upload Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video Kaynağı *
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="uploadMethod"
                  value="url"
                  checked={form.uploadMethod === 'url'}
                  onChange={(e) => setForm(prev => ({ ...prev, uploadMethod: e.target.value as 'url' | 'file', selectedFile: null }))}
                  className="mr-2"
                />
                URL ile
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="uploadMethod"
                  value="file"
                  checked={form.uploadMethod === 'file'}
                  onChange={(e) => setForm(prev => ({ ...prev, uploadMethod: e.target.value as 'url' | 'file', videoUrl: '' }))}
                  className="mr-2"
                />
                Dosya Yükle
              </label>
            </div>
          </div>

          {/* Video URL - Only show when URL method is selected */}
          {form.uploadMethod === 'url' && (
            <div>
              <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-700 mb-2">
                Video URL *
              </label>
              <input
                type="url"
                id="videoUrl"
                name="videoUrl"
                value={form.videoUrl}
                onChange={handleInputChange}
                required
                className={getFieldClassName('mediaUrl', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                placeholder="https://example.com/video.mp4"
              />
              {renderFieldError('mediaUrl')}
            </div>
          )}

          {/* File Upload - Only show when file method is selected */}
          {form.uploadMethod === 'file' && (
            <div>
              <label htmlFor="videoFile" className="block text-sm font-medium text-gray-700 mb-2">
                Video Dosyası *
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="videoFile"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Dosya seç</span>
                      <input
                        id="videoFile"
                        name="videoFile"
                        type="file"
                        accept="video/mp4,video/avi,video/mov,video/wmv,video/flv,video/webm"
                        onChange={handleFileSelect}
                        required
                        className="sr-only"
                      />
                    </label>
                    <p className="pl-1">veya sürükle bırak</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    MP4, AVI, MOV, WMV, FLV, WebM (maks. 500MB)
                  </p>
                  {form.selectedFile && (
                    <div className="mt-2 text-sm text-green-600">
                      ✓ Seçilen dosya: {form.selectedFile.name} ({(form.selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Thumbnail URL */}
          <div>
            <label htmlFor="thumbnailUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Thumbnail URL
            </label>
            <input
              type="url"
              id="thumbnailUrl"
              name="thumbnailUrl"
              value={form.thumbnailUrl}
              onChange={handleInputChange}
              className={getFieldClassName('thumbnailUrl', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
              placeholder="https://example.com/thumbnail.jpg"
            />
            {renderFieldError('thumbnailUrl')}
          </div>

          {/* Süre ve Kategori */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
                Süre (saniye)
              </label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={form.duration}
                onChange={handleInputChange}
                min="1"
                step="0.1"
                className={getFieldClassName('durationSeconds', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                placeholder="0"
              />
              {renderFieldError('durationSeconds')}
            </div>

            <div style={{ position: 'relative', overflow: 'visible', zIndex: 1001 }}>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                İçerik Türü *
              </label>
              <select
                id="category"
                name="category"
                value={form.category}
                onChange={handleInputChange}
                onClick={() => console.log('🔍 DEBUG: Dropdown clicked')}
                onFocus={() => console.log('🔍 DEBUG: Dropdown focused')}
                required
                className={getFieldClassName('contentType', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                style={{
                  position: 'relative !important' as any,
                  zIndex: '1002 !important' as any,
                  appearance: 'auto !important' as any,
                  WebkitAppearance: 'menulist !important' as any,
                  overflow: 'visible !important' as any,
                  height: 'auto !important' as any,
                  minHeight: '42px !important' as any,
                  background: 'white !important' as any,
                  border: '1px solid #d1d5db !important' as any
                }}
              >
                <option value="">İçerik türü seçin</option>
                <option value="movie">Film</option>
                <option value="series">Dizi</option>
                <option value="documentary">Belgesel</option>
                <option value="live">Canlı Yayın</option>
              </select>
              {renderFieldError('contentType')}
            </div>
          </div>

          {/* Dizi Bilgileri - Sadece dizi seçildiğinde göster */}
          {form.category === 'series' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <label htmlFor="totalEpisodes" className="block text-sm font-medium text-gray-700 mb-2">
                  Toplam Bölüm Sayısı
                </label>
                <input
                  type="number"
                  id="totalEpisodes"
                  name="totalEpisodes"
                  value={form.totalEpisodes}
                  onChange={handleInputChange}
                  min="1"
                  className={getFieldClassName('totalEpisodes', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                  placeholder="1"
                />
                {renderFieldError('totalEpisodes')}
              </div>

              <div>
                <label htmlFor="episodeNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Bölüm Numarası
                </label>
                <input
                  type="number"
                  id="episodeNumber"
                  name="episodeNumber"
                  value={form.episodeNumber}
                  onChange={handleInputChange}
                  min="1"
                  max={form.totalEpisodes}
                  className={getFieldClassName('episodeNumber', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                  placeholder="1"
                />
                {renderFieldError('episodeNumber')}
              </div>

              <div>
                <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 mb-2">
                  Grup İsmi
                </label>
                <input
                  type="text"
                  id="groupName"
                  name="groupName"
                  value={form.groupName}
                  onChange={handleInputChange}
                  className={getFieldClassName('groupName', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                  placeholder="Örn: Breaking Bad Sezon 1"
                />
                {renderFieldError('groupName')}
                <p className="text-xs text-gray-500 mt-1">
                  Kampanyalarda bu grup ismi ile filtreleme yapabilirsiniz
                </p>
              </div>
            </div>
          )}

          {/* Harici Sistem Bilgileri */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="thirdPartyId" className="block text-sm font-medium text-gray-700 mb-2">
                Harici Sistem ID
              </label>
              <input
                type="text"
                id="thirdPartyId"
                name="thirdPartyId"
                value={form.thirdPartyId}
                onChange={handleInputChange}
                className={getFieldClassName('thirdPartyId', 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                placeholder="Harici sistemden gelen ID"
              />
              {renderFieldError('thirdPartyId')}
              <p className="text-xs text-gray-500 mt-1">
                Harici sistemlerle entegrasyon için kullanılır
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yayın URL'leri
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={broadcastUrlInput}
                    onChange={(e) => setBroadcastUrlInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBroadcastUrl())}
                    className={getFieldClassName('broadcastUrls', 'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                    placeholder="https://broadcast-url.com/stream"
                  />
                  <button
                    type="button"
                    onClick={handleAddBroadcastUrl}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Ekle
                  </button>
                </div>
                <div className="space-y-2">
                  {form.broadcastUrls.map((url, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-100 rounded-md"
                    >
                      <span className="text-sm text-gray-700 flex-1 truncate">{url}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveBroadcastUrl(url)}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                {renderFieldError('broadcastUrls')}
                <p className="text-xs text-gray-500">
                  Çoklu yayın kaynakları için URL'ler ekleyebilirsiniz
                </p>
              </div>
            </div>
          </div>

          {/* Etiketler */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Etiketler
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                className={getFieldClassName('tags', 'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2')}
                placeholder="Etiket ekle"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Ekle
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            {renderFieldError('tags')}
          </div>

          {/* Transcribe Bilgisi */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Otomatik Transcribe
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Video eklendikten sonra AWS Transcribe ile otomatik olarak analiz edilecek ve 
                    shoppable ads için anahtar kelimeler çıkarılacak.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/content')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'İşleniyor...' : 'İçerik Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContent;